import {
  Close as CloseIcon,
  KeyboardArrowUp as MaximizeIcon,
  NotificationsNone as BellIcon,
  KeyboardArrowDown as MinimizeIcon,
} from '@mui/icons-material';
import { Box, Chip, IconButton, Tooltip, Typography } from '@pulse/ui';

import { useFloatingPanel } from './FloatingPanelContext';

export interface NotificationPanelHeaderProps {
  unreadCount: number;
}

// `unreadCount` is a prop (not read from a notifications context) so this header stays
// reusable as a FloatingPanel `header` slot even for non-notification content later.
export function NotificationPanelHeader({ unreadCount }: NotificationPanelHeaderProps) {
  const { title, minimized, onMinimize, onClose } = useFloatingPanel();

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={minimized ? 'Expand notifications panel' : 'Minimize notifications panel'}
      onClick={onMinimize}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onMinimize();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.25,
        bgcolor: 'background.paper',
        userSelect: 'none',
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {/* Left: bell icon + title + chip */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <BellIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          {unreadCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: -4,
                right: -5,
                minWidth: 16,
                height: 16,
                borderRadius: '8px',
                bgcolor: 'primary.main',
                color: 'common.white',
                fontSize: '0.6rem',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 0.4,
                lineHeight: 1,
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Box>
          )}
        </Box>

        <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
          {title}
        </Typography>

        {unreadCount > 0 && (
          <Chip
            label={`${unreadCount} new`}
            size="small"
            color="primary"
            sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
          />
        )}
      </Box>

      {/* Right: minimize + close */}
      <Box sx={{ display: 'flex', gap: 0.25 }}>
        <Tooltip title={minimized ? 'Expand' : 'Minimize'} placement="top">
          {/* stopPropagation — the whole row already toggles minimize on click (see the
          outer Box above); without this, clicking the icon would bubble up and fire
          onMinimize a second time, cancelling itself out. */}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMinimize();
            }}
            sx={{ p: 0.5, color: 'text.secondary' }}
          >
            {minimized ? (
              <MaximizeIcon sx={{ fontSize: 18 }} />
            ) : (
              <MinimizeIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Close" placement="top">
          {/* stopPropagation — Close must not also toggle minimize via the row's onClick. */}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            sx={{ p: 0.5, color: 'text.secondary' }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}